/**
 * RENDER module — extended post-pipeline configuration.
 *
 * NoirRenderConfig (frozen core) carries the contract knobs; the render
 * module adds its own grade/bloom knobs here without touching core.
 * Every knob is runtime-settable via the pipeline's setConfig.
 */
import type { NoirRenderConfig } from '../core/types'
import {
  DEFAULT_BLACK_POINT,
  DEFAULT_BLOOM_RADIUS,
  DEFAULT_BLOOM_STRENGTH,
  DEFAULT_BLOOM_THRESHOLD,
  DEFAULT_BW_MIX,
  DEFAULT_GRAIN_SIZE_PX,
  DEFAULT_SEPIA_AMOUNT,
  DEFAULT_WHITE_POINT
} from './constants'

export interface NoirPostConfig extends NoirRenderConfig {
  /** 0..1 — luminance B&W mix (1 = full mono). */
  readonly bwMix: number
  /** 0..1 — slight sepia lift over the mono grade. */
  readonly sepiaAmount: number
  /** Levels black point — values below crush to black. */
  readonly blackPoint: number
  /** Levels white point — values above bloom to white. */
  readonly whitePoint: number
  /** Bloom strength; 0 disables the bloom pass entirely. */
  readonly bloomStrength: number
  readonly bloomRadius: number
  /** HDR luminance threshold — keeps bloom selective to emissive/neon. */
  readonly bloomThreshold: number
  /** Grain cell size in physical pixels. */
  readonly grainSizePx: number
}

/** Merge the core contract config with the render module's grade defaults. */
export const toPostConfig = (config: NoirRenderConfig): NoirPostConfig => ({
  ...config,
  bwMix: DEFAULT_BW_MIX,
  sepiaAmount: DEFAULT_SEPIA_AMOUNT,
  blackPoint: DEFAULT_BLACK_POINT,
  whitePoint: DEFAULT_WHITE_POINT,
  bloomStrength: DEFAULT_BLOOM_STRENGTH,
  bloomRadius: DEFAULT_BLOOM_RADIUS,
  bloomThreshold: DEFAULT_BLOOM_THRESHOLD,
  grainSizePx: DEFAULT_GRAIN_SIZE_PX
})
